import { SelectionModel } from '@angular/cdk/collections';
import { Injectable, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import { ActivatedRoute, PRIMARY_OUTLET } from '@angular/router';
import { Actions } from '@config/actions.config';
import { AppConfig } from '@config/app.config';
import { SetMenu } from '@config/menu.config';
import { RolesService } from '@core/services/roles.service';
import { TransfersService } from '@core/services/transfers.service';
import { Constants } from '@shared/class/constants.class';
import { AlertDeleteViewComponent } from '@shared/components/alert-delete-view/alert-delete-view.component';
import { AlertViewComponent } from '@shared/components/alert-view/alert-view.component';
import { LoadingViewComponent } from '@shared/components/loading-view/loading-view.component';
import { NotificationViewComponent } from '@shared/components/notification-view/notification-view.component';
import { AccessDirect } from '@shared/interfaces/access-direct';
import { Breadcrumb } from '@shared/interfaces/breadcrumb';
import { Menu, Second } from '@shared/interfaces/menu';
import { Error } from '@shared/interfaces/result';
import { BcAdalAngularService } from 'bc-adal-angular';
import * as crypto from 'crypto-js';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GeneralsService {
  public constants = new Constants();

  private deleteSource = new BehaviorSubject<boolean>(false);
  private breadcrumbSource = new BehaviorSubject<Array<Breadcrumb>>([]);
  private itemMenuSource = new BehaviorSubject<any>([]);
  private menuSource = new BehaviorSubject<boolean>(false);
  private actionSource = new BehaviorSubject<boolean>(false);
  private transferSource = new BehaviorSubject<boolean>(false);
  public delete$ = this.deleteSource.asObservable();
  public breadcrumb$ = this.breadcrumbSource.asObservable();
  public itemMenu$ = this.itemMenuSource.asObservable();
  public menu$ = this.menuSource.asObservable();
  public action$ = this.actionSource.asObservable();
  public transfer$ = this.transferSource.asObservable();
  public userName = '';

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public appConfig: AppConfig,
    public dialog: MatDialog,
    public rolesService: RolesService,
    public transfersService: TransfersService,
    public adalService: BcAdalAngularService
  ) {}

  getUserName() {
    if (this.adalService.isAuthenticated) {
      this.userName = this.adalService.userInfo.userName;
      const index =
        this.userName.indexOf('@') < 0
          ? this.userName.length
          : this.userName.indexOf('@');
      this.userName = this.userName.substr(0, index);
    } else {
      this.userName = '';
    }

    return this.userName;
  }

  setNewMenu() {
    return new SetMenu();
  }

  generateMessageId() {
    const keys = '123456$#@$^@1ERF';
    const key = crypto.enc.Utf8.parse(keys);
    const iv = crypto.enc.Utf8.parse(keys);
    const date = moment().format('YYYYMMDDHHmmssSSS');
    const encrypted = crypto.AES.encrypt(crypto.enc.Utf8.parse(date), key, {
      keySize: 128 / 8,
      iv: iv,
      mode: crypto.mode.CBC,
      padding: crypto.pad.Pkcs7
    });
    return encrypted.toString().substring(0, 20);
  }

  modal(component: any, data: any, name?: string) {
    const panelClass = ['mat-modal'];
    panelClass.push(name);

    return this.dialog.open(component, {
      data: data,
      disableClose: true,
      panelClass: panelClass,
      autoFocus: false
    });
  }

  alert(message: string, cancel: boolean = true) {
    return this.dialog.open(AlertViewComponent, {
      data: { content: message, cancel: cancel },
      disableClose: true,
      panelClass: 'mat-alert'
    });
  }

  notification(type: string, message?: any) {
    if (message === false) {
      return message;
    } else {
      return this.dialog.open(NotificationViewComponent, {
        data: {
          type: type,
          content: message
        },
        panelClass: 'mat-notification'
      });
    }
  }

  loading(name?: string) {
    const panelClass = ['mat-loading'];
    panelClass.push(name);

    return this.dialog.open(LoadingViewComponent, {
      panelClass: panelClass,
      disableClose: true
    });
  }

  setMenu(menu: any) {
    this.itemMenuSource.next(menu);
  }

  getMenu() {
    this.rolesService.getItemUser().subscribe(
      result => {
        const errorResult = { status: 1 };
        if (result && result.length > 0) {
          const access = this.debugAccess(result);
          const menu = this.setNewMenu();
          const newMenu: Array<Menu> = [];
          let validate: boolean;
          for (let i = 0; i < menu.menu.length; i++) {
            const parent = menu.menu[i];
            if (parent.show) {
              switch (parent.type) {
                case this.appConfig.TYPE_ACCORDION:
                  let childs = 0;
                  for (let j = 0; j < parent.child.length; j++) {
                    const child = parent.child[j];
                    if (child.show) {
                      validate = child.show = this.validatePrimary(
                        child.access.primary.service,
                        access
                      );
                      if (validate) {
                        child.access.second = this.validateSecond(
                          child.access.second,
                          access
                        );
                        childs++;
                      }
                    }
                  }
                  if (childs > 0) {
                    newMenu.push(parent);
                  }
                  break;
                case this.appConfig.TYPE_SINGLE:
                  validate = parent.show = this.validatePrimary(
                    parent.access.primary.service,
                    access
                  );
                  if (validate) {
                    parent.access.second = this.validateSecond(
                      parent.access.second,
                      access
                    );
                    newMenu.push(parent);
                  }
                  break;
              }
            }
          }
          if (newMenu.length > 0) {
            this.setMenu(this.debugMenu(newMenu));
            this.actionSource.next(true);
          } else {
            this.setMenu(errorResult);
          }
        } else {
          this.setMenu(errorResult);
        }
      },
      error => {
        this.setMenu(error);
      }
    );
  }

  validatePrimary(primary: Array<string>, access: any): boolean {
    if (primary && primary.length) {
      let permits = 0;
      primary.forEach(element => {
        if (access.indexOf(element) >= 0) {
          permits++;
        }
      });
      if (permits === primary.length) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  validateSecond(second: Array<Second>, access: any): Array<Second> {
    if (second && second.length) {
      second.forEach(element => {
        const service = element.service;
        if (service.length) {
          let permits = 0;
          service.forEach(elementService => {
            if (access.indexOf(elementService) >= 0) {
              permits++;
            }
          });
          if (permits === service.length) {
            element.show = true;
          } else {
            element.show = false;
          }
        } else {
          element.show = false;
        }
      });
      return second;
    } else {
      return second;
    }
  }

  validateAction(action: string, location: string): boolean {
    const menu = this.itemMenuSource.value;
    let result = false;
    menu.forEach((parent: any) => {
      let access: any = '';
      if (parent.type === this.appConfig.TYPE_ACCORDION) {
        parent.child.forEach((child: any) => {
          if (child.id === location) {
            access = child.access.second;
          }
        });
      } else if (parent.type === this.appConfig.TYPE_SINGLE) {
        if (parent.id === location) {
          access = parent.access.second;
        }
      }

      if (access.length > 0) {
        access.forEach((element: any) => {
          if (element.type === action) {
            result = element.show;
          }
        });
      }
    });
    return result;
  }

  clearMenu() {
    this.setMenu([]);
  }

  logoutMenu() {
    this.setMenu({ status: 401 });
    this.dialog.closeAll();
  }

  debugMenu(data: any): Array<Menu> {
    const menu: Array<Menu> = [];
    if (!data.status && data.length) {
      const menuConfig = data;
      let newChild: Array<any> = [],
        newElement: any;
      menuConfig.forEach((element: any) => {
        newChild = [];
        if (element.show) {
          newElement = element;
          if (element.type === this.appConfig.TYPE_ACCORDION) {
            element.child.forEach((child: any) => {
              if (child.show) {
                newChild.push(child);
              }
            });
          }
          newElement.child = newChild;
          menu.push(newElement);
        }
      });
    }
    return menu;
  }

  debugRoles(data: Array<Menu>): Array<Menu> {
    const menu: Array<Menu> = [];
    data.forEach((element: any) => {
      const newChild: Array<any> = [];
      let newElement: any, service: any;
      if (element.type === this.appConfig.TYPE_ACCORDION) {
        element.child.forEach((child: any) => {
          service = child.access.primary.service;
          if (service.length > 0) {
            newChild.push(child);
          }
        });
        if (newChild.length > 0) {
          newElement = element;
          newElement.child = newChild;
          menu.push(newElement);
        }
      } else if (element.type === this.appConfig.TYPE_SINGLE) {
        service = element.access.primary.service;
        if (service.length > 0) {
          menu.push(element);
        }
      }
    });
    return menu;
  }

  debugDirectAccess(data: Array<Menu>): Array<AccessDirect> {
    const directAccess: Array<AccessDirect> = [];
    const menu = this.debugMenu(data);
    menu.forEach(element => {
      let access: AccessDirect;
      if (element.show && element.home) {
        if (element.type === this.appConfig.TYPE_SINGLE) {
          access = {
            name: element.name,
            icon: element.icon,
            link: element.link
          };
          directAccess.push(access);
        } else {
          element.child.forEach(child => {
            if (child.show && child.home) {
              access = { name: child.name, icon: child.icon, link: child.link };
              directAccess.push(access);
            }
          });
        }
      }
    });
    return directAccess;
  }

  debugAccess(access: any): Array<string> {
    const newAccess: Array<string> = [];
    access.forEach((element: any) => {
      newAccess.push(element.code);
    });
    return newAccess;
  }

  getBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Breadcrumb[] = []
  ) {
    const ROUTE_DATA_BREADCRUMB = 'breadcrumb';
    const ROUTE_DATA_PARENT = 'parent';
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      if (child.outlet !== PRIMARY_OUTLET) {
        continue;
      }

      if (!child.snapshot.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB)) {
        this.getBreadcrumbs(child, url, breadcrumbs);
      }

      const routeURL: string = child.snapshot.url
        .map(segment => segment.path)
        .join('/');

      url += `/${routeURL}`;

      if (
        child.snapshot.data[ROUTE_DATA_BREADCRUMB] !== undefined &&
        child.snapshot.data[ROUTE_DATA_PARENT] !== undefined
      ) {
        const breadcrumb: Breadcrumb = {
          label: child.snapshot.data[ROUTE_DATA_BREADCRUMB],
          parent: child.snapshot.data[ROUTE_DATA_PARENT],
          url: url
        };
        breadcrumbs.push(breadcrumb);
        this.breadcrumbSource.next(breadcrumbs);
      }

      this.getBreadcrumbs(child, url, breadcrumbs);
    }
    return breadcrumbs;
  }

  resetBreadcrumb() {
    this.breadcrumbSource.next([]);
  }

  getActions(columns: Array<string>, actions: Array<string>, location: string) {
    const newActions = new Actions(),
      result = { displayedColumns: columns, displayedActions: newActions };
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      const validate = this.validateAction(action, location);
      result.displayedActions[action] = validate;
      if (!validate) {
        const index = columns.indexOf(action);
        if (index >= 0) {
          columns.splice(index, 1);
        }
      }
    }
    result.displayedColumns = columns;
    return result;
  }

  export(filename: string, data: any) {
    const extension = 'csv';
    this.download('export_' + filename + '.' + extension, extension, {
      body: data
    });
  }

  download(fileName: string, extension: string, data: any) {
    const object = new Blob([data.body], { type: extension });
    const url = window.URL.createObjectURL(object);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }

  getFileName(headers: any) {
    let fileName = 'fileName';
    if (headers) {
      headers = headers.split(';');
      if (headers[1]) {
        const newfileName = headers[1].split('=');
        if (newfileName[1]) {
          fileName = newfileName[1];
        }
      }
    }
    return fileName;
  }

  messageSelectedDelete(
    type: string,
    selectedLength: number,
    dataLength: number
  ) {
    let message: any = '',
      count: any = '';

    if (selectedLength === dataLength) {
      count = 'all';
    } else if (selectedLength === 1) {
      count = 1;
    } else {
      count = selectedLength;
    }

    switch (type) {
      case 'confirm': {
        switch (count) {
          case 'all': {
            message = this.appConfig.LANG.question.deleteAll;
            break;
          }
          case 1: {
            message = this.appConfig.LANG.question.deleteOne;
            break;
          }
          default: {
            message =
              this.appConfig.LANG.question.deleteElementsStart +
              count +
              this.appConfig.LANG.question.deleteElementsEnd;
            break;
          }
        }
        break;
      }
      case 'result': {
        switch (count) {
          case 'all': {
            message = this.appConfig.LANG.message.deleteAll;
            break;
          }
          case 1: {
            message = this.appConfig.LANG.message.deleteOne;
            break;
          }
          default: {
            message = count + this.appConfig.LANG.message.deleteElements;
            break;
          }
        }
        break;
      }
      case 'label': {
        switch (count) {
          case 'all': {
            message = this.appConfig.LANG.message.deletedAll;
            break;
          }
          case 0: {
            message = false;
            break;
          }
          default: {
            message = this.appConfig.LANG.message.deletedElements.replace(
              '{COUNT}',
              count
            );
            break;
          }
        }
        break;
      }
      default: {
        break;
      }
    }
    return message;
  }

  reload(self: any) {
    self.loadData();
    self.labelDelete = false;

    if (self.filterInput) {
      self.filterInput.nativeElement.value = '';
    }
  }

  destroySubscription() {
    this.constants.destroy$.next(true);
    this.constants.destroy$.unsubscribe();
  }

  returnDialog(dialogLoading: any, dialogRef: any, type: any, error?: Error) {
    dialogLoading.close();
    if (type === this.appConfig.NOTIFICATION.success) {
      dialogRef.close(200);
    } else {
      this.notification(
        this.appConfig.NOTIFICATION.error,
        this.showError(error)
      );
    }
  }

  returnDelete(dataLength: number, success: number, errors: Array<string>) {
    let messageSuccess: string = success.toString();
    if (success > 0) {
      messageSuccess = this.messageSelectedDelete(
        'result',
        success,
        dataLength
      );
    }

    if (dataLength === success) {
      this.notification(this.appConfig.NOTIFICATION.success, messageSuccess);
    } else {
      this.dialog.open(AlertDeleteViewComponent, {
        data: {
          success: messageSuccess,
          error: errors
        },
        disableClose: true,
        panelClass: 'mat-alert-delete'
      });
    }
  }

  showError(error: Error) {
    let textError: any =
      error.status + ': ' + this.appConfig.LANG.message.errorFatal;
    if (error.status === 401) {
      textError = false;
    } else if (
      error &&
      error.error &&
      error.error.errors &&
      error.error.errors.length
    ) {
      const errors = error.error.errors[0];
      textError = errors.code + ': ' + errors.detail;
    }

    return textError;
  }

  save(
    action: string,
    form: FormGroup,
    dialogRef: MatDialogRef<any>,
    service: any,
    message?: string
  ) {
    let dialogLoading: any;
    dialogLoading = this.loading();

    const observer = {
      next: () =>
        this.returnDialog(
          dialogLoading,
          dialogRef,
          this.appConfig.NOTIFICATION.success
        ),
      error: (error: any) =>
        this.returnDialog(
          dialogLoading,
          dialogRef,
          this.appConfig.NOTIFICATION.error,
          error
        )
    };

    if (action === this.appConfig.ACTIONS.add.id) {
      service
        .add(form.value)
        .pipe(takeUntil(this.constants.destroy$))
        .subscribe(observer);
    } else if (action === this.appConfig.ACTIONS.edit.id) {
      message = message ? message : this.appConfig.LANG.question.edit;
      const dialog = this.alert(message);

      dialog
        .afterClosed()
        .pipe(takeUntil(this.constants.destroy$))
        .subscribe(result => {
          switch (result) {
            case 200:
              service
                .edit(form.value)
                .pipe(takeUntil(this.constants.destroy$))
                .subscribe(observer);
              break;
            case 300:
              dialogLoading.close();
              this.notification(
                this.appConfig.NOTIFICATION.info,
                this.appConfig.LANG.message.noEdit
              );
              break;

            default:
              dialogLoading.close();
              this.notification(this.appConfig.NOTIFICATION.fatalError);
              break;
          }
        });
    } else if (action === this.appConfig.ACTIONS.editId.id) {
      message = message ? message : this.appConfig.LANG.question.edit;
      const dialog = this.alert(message);

      dialog
        .afterClosed()
        .pipe(takeUntil(this.constants.destroy$))
        .subscribe(result => {
          switch (result) {
            case 200:
              service
                .editId(form.value)
                .pipe(takeUntil(this.constants.destroy$))
                .subscribe(observer);
              break;
            case 300:
              dialogLoading.close();
              this.notification(
                this.appConfig.NOTIFICATION.info,
                this.appConfig.LANG.message.noEdit
              );
              break;

            default:
              dialogLoading.close();
              this.notification(this.appConfig.NOTIFICATION.fatalError);
              break;
          }
        });
    }
  }

  delete(
    selected: any,
    selectedLength: number,
    dataLength: number,
    service: any
  ) {
    let count = 0;
    let successCount = 0;
    let dialogLoading: any;
    const errors: Array<string> = [];

    dialogLoading = this.loading();
    selected.forEach((element: any) => {
      service
        .delete(element.id)
        .pipe(takeUntil(this.constants.destroy$))
        .subscribe(
          () => {
            count++;
            successCount++;
            if (selectedLength === count) {
              dialogLoading.close();
              this.finishDelete(dataLength, successCount, errors);
            }
          },
          (error: any) => {
            count++;
            errors.push(element.id + ': ' + this.showError(error));

            if (selectedLength === count) {
              dialogLoading.close();
              this.finishDelete(dataLength, successCount, errors);
            }
          }
        );
    });
  }

  finishDelete(
    dataLength: number,
    successCount: number,
    errors: Array<string>
  ) {
    this.returnDelete(dataLength, successCount, errors);
    this.deleteSource.next(true);
  }

  applyFilter(dataSource: MatTableDataSource<any>, filterValue: string) {
    dataSource.paginator.firstPage();
    dataSource.filter = filterValue.trim().toLowerCase();
  }

  isAllSelected(
    dataSource: MatTableDataSource<any>,
    selection: SelectionModel<any>
  ) {
    return selection.selected.length === dataSource.data.length;
  }

  masterToggle(
    dataSource: MatTableDataSource<any>,
    selection: SelectionModel<any>
  ) {
    this.isAllSelected(dataSource, selection)
      ? selection.clear()
      : dataSource.data.forEach(row => selection.select(row));
  }

  afterClosed(self: any, result: string | number, message: string) {
    if (result === 200) {
      this.notification(this.appConfig.NOTIFICATION.success, message);
      self.reload();
    } else if (!result) {
      this.notification(this.appConfig.NOTIFICATION.fatalError);
    }
  }

  returnName(
    firstName: string,
    firstLastName: string,
    secondName: string,
    secondLastName: string
  ) {
    let result = '';
    if (firstName) {
      result = firstName;
      if (firstLastName) {
        result = result + ' ' + firstLastName;
      }
    } else if (firstLastName) {
      result = firstLastName;
    }

    if (secondName) {
      if (result) {
        result = result + ' ' + secondName;
      } else {
        result = secondName;
      }
      if (secondLastName) {
        result = result + ' ' + secondLastName;
      }
    } else if (secondLastName) {
      if (result) {
        result = result + ' ' + secondLastName;
      } else {
        result = secondLastName;
      }
    }

    return result;
  }

  startMenu(event: any) {
    this.menuSource.next(event.target.checked);
  }

  hiddenMenu() {
    this.menuSource.next(false);
  }

  openTransfer(form: any, component: any) {
    const loading = this.loading();

    this.transfersService
      .getItemId(form)
      .pipe(takeUntil(this.constants.destroy$))
      .subscribe(
        result => {
          loading.close();
          if (
            Object.keys(result).length > 0 &&
            Object.keys(result.data).length > 0
          ) {
            const data = result.data;
            const dialog = this.modal(component, data, 'mat-modal-big');
            dialog.afterClosed().subscribe((resultAfterClosed: any) => {
              if (resultAfterClosed === 100) {
                this.transferSource.next(true);
              }
            });
          } else {
            this.notification(
              this.appConfig.NOTIFICATION.info,
              this.appConfig.LANG.transfers.noTransfers
            );
          }
        },
        error => {
          loading.close();
          this.notification(
            this.appConfig.NOTIFICATION.error,
            this.showError(error)
          );
        }
      );
  }
}
